import React, { useState, useEffect } from 'react';
import axiosInstance from 'utils/axios-instance';
import Swal from 'sweetalert2';

type Notice = {
    id: number;
    communityUuid: string;
    title: string;
    content: string;
    isPinned: boolean;
    createdBy: number;
    createdByNickname: string;
    createdAt: string;
    updatedAt: string;
}

interface Props {
    communityUuid: string;
}

const NoticeManagement: React.FC<Props> = ({ communityUuid }) => {
    const [notices, setNotices] = useState<Notice[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editingNotice, setEditingNotice] = useState<Partial<Notice> | null>(null);

    useEffect(() => {
        fetchNotices();
    }, [communityUuid]);

    const fetchNotices = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(
                `/api/community/admin/notice/list?communityUuid=${communityUuid}`
            );
            if (response.data.statusCode === 200) {
                setNotices(response.data.notices);
            }
        } catch (error) {
            console.error('공지사항 조회 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '공지사항을 불러오는 중 오류가 발생했습니다.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingNotice({
            title: '',
            content: '',
            isPinned: false,
            communityUuid: communityUuid
        });
        setIsEditing(true);
    };

    const handleEdit = (notice: Notice) => {
        setEditingNotice(notice);
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!editingNotice) return;

        // 유효성 검사
        if (!editingNotice.title || editingNotice.title.trim().length < 2) {
            Swal.fire({
                icon: 'warning',
                title: '입력 오류',
                text: '제목을 2자 이상 입력해주세요.',
            });
            return;
        }

        if (!editingNotice.content || editingNotice.content.trim().length < 10) {
            Swal.fire({
                icon: 'warning',
                title: '입력 오류',
                text: '내용을 10자 이상 입력해주세요.',
            });
            return;
        }

        try {
            const isUpdate = !!editingNotice.id;
            const url = '/api/community/admin/notice';
            const method = isUpdate ? 'put' : 'post';

            const payload: any = {
                communityUuid,
                title: editingNotice.title,
                content: editingNotice.content,
                isPinned: editingNotice.isPinned || false
            };

            if (isUpdate) {
                payload.noticeId = editingNotice.id;
            }

            const response = await axiosInstance[method](url, payload);

            if (response.data.statusCode === 200) {
                Swal.fire({
                    icon: 'success',
                    title: '완료',
                    text: response.data.message,
                });
                setIsEditing(false);
                setEditingNotice(null);
                fetchNotices();
            }
        } catch (error: any) {
            console.error('공지사항 저장 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: error.response?.data?.message || '공지사항 저장 중 오류가 발생했습니다.',
            });
        }
    };

    const handleDelete = async (noticeId: number, title: string) => {
        const result = await Swal.fire({
            title: '공지사항 삭제',
            text: `"${title}"을(를) 삭제하시겠습니까?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '삭제',
            cancelButtonText: '취소',
            confirmButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            try {
                const response = await axiosInstance.delete('/api/community/admin/notice', {
                    data: {
                        noticeId,
                        communityUuid
                    }
                });

                if (response.data.statusCode === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: '완료',
                        text: response.data.message,
                    });
                    fetchNotices();
                }
            } catch (error: any) {
                console.error('공지사항 삭제 실패:', error);
                Swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: error.response?.data?.message || '공지사항 삭제 중 오류가 발생했습니다.',
                });
            }
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingNotice(null);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>공지사항을 불러오는 중...</p>
            </div>
        );
    }

    if (isEditing && editingNotice) {
        return (
            <div className="notice-editor">
                <div className="editor-header">
                    <h2>{editingNotice.id ? '공지사항 수정' : '공지사항 작성'}</h2>
                </div>
                
                <div className="editor-form">
                    <div className="form-group">
                        <label>제목</label>
                        <input
                            type="text"
                            placeholder="공지사항 제목을 입력하세요"
                            value={editingNotice.title || ''}
                            onChange={(e) => setEditingNotice({ ...editingNotice, title: e.target.value })}
                            maxLength={500}
                        />
                    </div>

                    <div className="form-group">
                        <label>내용</label>
                        <textarea
                            placeholder="공지사항 내용을 입력하세요"
                            value={editingNotice.content || ''}
                            onChange={(e) => setEditingNotice({ ...editingNotice, content: e.target.value })}
                            rows={10}
                        />
                    </div>

                    <div className="form-group checkbox-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={editingNotice.isPinned || false}
                                onChange={(e) => setEditingNotice({ ...editingNotice, isPinned: e.target.checked })}
                            />
                            <span>상단 고정</span>
                        </label>
                    </div>

                    <div className="editor-buttons">
                        <button className="save-button" onClick={handleSave}>
                            저장
                        </button>
                        <button className="cancel-button" onClick={handleCancel}>
                            취소
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="notice-management">
            <div className="management-header">
                <h2>공지사항 관리</h2>
                <button className="create-button" onClick={handleCreate}>
                    새 공지사항 작성
                </button>
            </div>

            {notices.length === 0 ? (
                <div className="empty-state">
                    <p>등록된 공지사항이 없습니다.</p>
                    <button className="create-button" onClick={handleCreate}>
                        첫 공지사항 작성하기
                    </button>
                </div>
            ) : (
                <div className="notices-list">
                    {notices.map(notice => (
                        <div key={notice.id} className={`notice-card ${notice.isPinned ? 'pinned' : ''}`}>
                            <div className="notice-header">
                                <h3>
                                    {notice.isPinned && <span className="pin-icon">📌 </span>}
                                    {notice.title}
                                </h3>
                                <div className="notice-actions">
                                    <button 
                                        className="edit-button"
                                        onClick={() => handleEdit(notice)}
                                    >
                                        수정
                                    </button>
                                    <button 
                                        className="delete-button"
                                        onClick={() => handleDelete(notice.id, notice.title)}
                                    >
                                        삭제
                                    </button>
                                </div>
                            </div>
                            <div className="notice-content">
                                <p>{notice.content}</p>
                            </div>
                            <div className="notice-meta">
                                <span>작성자: {notice.createdByNickname}</span>
                                <span>{new Date(notice.createdAt).toLocaleString('ko-KR')}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NoticeManagement;

